import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";

import CustomMap from "./components/CustomMap";
import Confirmation from "./components/modals/confirmation";
import NavBar from "./components/NavBar";
import FullScreenTable from "./components/modals/fullscreenTable";
import AddToMap from "./components/modals/addToMap";
import CircularProgress from "@mui/material/CircularProgress";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorModal from "./components/modals/ErrorModal";
import CreateReportModal from "./components/modals/CreateReportModal";
import SearchModal from "./components/modals/searchModal";
import QuickCapture from "./components/QuickCapture";

import withClearCache from "./ClearCache";
import { useAuth } from "./components/auth/AuthProvider";
import { usePermissions } from "./hooks/usePermissions";

function App() {
  
  // If user is provided via props, use their lat/lng; otherwise default to these coords
  const { user } = useAuth();
  const { canWrite, canDelete, isReadOnly, roleName } = usePermissions();
  const defaultCoords = user
    ? { lat: user.Latitude, lng: user.Longitude }
    : { lat: 36.144626, lng: -97.291164 };
  // --------------------
  // State (was this.state)
  // --------------------
  const [markers, setMarkers] = useState([]);
  const [originalMarkers, setOriginalMarkers] = useState([]);
  const [originalLines, setOriginalLines] = useState([]);
  const [lines, setLines] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const quickCaptureRef = useRef(null);
  const [addedLatLng, setAddedLatLng] = useState([]);
  const [addedLinesLatLng, setAddedLinesLatLng] = useState([]);
  const [geocodedFields, setGeocodedFields] = useState(null);
  const [cancelMarker, setCancelMarker] = useState(false);
  const [markerData, setMarkerData] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [deleteMarkerID, setDeleteMarkerID] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const [service, setService] = useState("Hydrant");
  const [serviceData, setServiceData] = useState([]);
  const [fullScreen, setFullScreen] = useState(false);
  const [movingMarker, setMovingMarker] = useState(false);
  const [resetMarker, setResetMarker] = useState(false);
  // const [user, setUser] = useState(initialUser);
  const [closeAllPopups, setCloseAllPopups] = useState(false);
  const [showFormAndAddMarker, setShowFormAndAddMarker] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [geoCodedCenter, setGeoCodedCenter] = useState([
    defaultCoords.lat,
    defaultCoords.lng,
  ]);
  const [mapCenter, setMapCenter] = useState([]);
  const [showSearchFormOnMap, setShowSearchFormOnMap] = useState(false);
  const [addingPolyLine, setAddingPolyLine] = useState(false);
  const [clearLinesTrigger, setClearLinesTrigger] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [markerFormsProps, setMarkerFormsProps] = useState({});
  const [zoom, setZoom] = useState(undefined);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [showCreateReport, setShowCreateReport] = useState(false);
  const [ErrorText, setErrorText] = useState(null);
  const [highlightedMarkers, setHighlightedMarkers] = useState([]);
  const [addedAssetType, setAddedAssetType] = useState("");
  const [previewMarkerUpdate, setPreviewMarkerUpdate] = useState(null);
  

  // Intervals in useRef so we can clear them later
  const progressInterval = useRef(null);
  const refreshInterval = useRef(null);
  
  // Ref for addingPolyLine to provide immediate access to current value
  const addingPolyLineRef = useRef(addingPolyLine);

  // Debug useEffect to track addingPolyLine state changes
  useEffect(() => {
    console.log("addingPolyLine state changed to:", addingPolyLine);
    // Update ref whenever state changes
    addingPolyLineRef.current = addingPolyLine;
  }, [addingPolyLine]);

  const filterMarkers = (markers) => {
    const stored = localStorage.getItem("markerFilters");
    if (stored) {
      const filters = stored.split(",");
      return markers.filter((marker) => filters.includes(marker.assetType));
    }
    return markers;
  }

  const setFilterMarkersFromNavbar = () => {
    const stored = localStorage.getItem("markerFilters");
    if (stored) {
      const filters = stored.split(",");
      const tempMarkers = originalMarkers.filter((marker) => filters.includes(marker.assetType));
      setMarkers(tempMarkers);
      const tempLines = originalLines.filter((marker) => filters.includes(marker.assetType));
      setLines(tempLines);
    } else {
      setMarkers([]);
      setLines([]);
    }
    
  };
  // ----------------------------
  // componentDidMount equivalent
  // ----------------------------
  const ASSET_CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache TTL

  // Helper to invalidate client-side cache
  const invalidateAssetCache = useCallback(() => {
    sessionStorage.removeItem('assetData');
    sessionStorage.removeItem('assetDataTimestamp');
    console.log('[Client Cache] Invalidated asset cache');
  }, []);

  useEffect(() => {
    let mounted = true;

    (async () => {
      // A "progress" timer that increments every 0.8s
      progressInterval.current = setInterval(() => {
        setProgress((prev) => (prev >= 100 ? 0 : prev + 10));
      }, 800);

      // Refresh token every 10 minutes
      refreshInterval.current = setInterval(() => {
        axios
          .get("/auth/refresh")
          .then(() => console.log("refreshed"))
          .catch((error) => console.log("error refreshing token", error));
      }, 600000);

      // Fetch hydrants and lines
      try {
        if (markers.length === 0) {
          // Check sessionStorage cache first
          const cachedData = sessionStorage.getItem('assetData');
          const cacheTimestamp = sessionStorage.getItem('assetDataTimestamp');
          
          if (cachedData && cacheTimestamp && 
              Date.now() - parseInt(cacheTimestamp) < ASSET_CACHE_TTL) {
            // Use cached data
            console.log('[Client Cache] Using cached asset data');
            const parsed = JSON.parse(cachedData);
            if (mounted) {
              setOriginalMarkers(parsed.result);
              setOriginalLines(parsed.lines);
              const filteredMarkers = filterMarkers(parsed.result);
              const filteredLines = filterMarkers(parsed.lines);
              setMarkers(filteredMarkers);
              setLines(filteredLines);
              setServiceData(parsed.result);
              setIsLoading(false);
            }
          } else {
            // Fetch fresh data
            console.log('[Client Cache] Fetching fresh asset data');
            const { data } = await axios.get("/api/hydrants");
            if (mounted) {
              if (data && data.message === "No token provided") {
                window.location = "/login";
              } else {
                // Try to cache the response in sessionStorage (may fail if data too large)
                try {
                  sessionStorage.setItem('assetData', JSON.stringify({
                    result: data.result[0],
                    lines: data.lines[0]
                  }));
                  sessionStorage.setItem('assetDataTimestamp', Date.now().toString());
                } catch (cacheErr) {
                  console.warn('[Client Cache] Data too large for sessionStorage, skipping cache');
                }
                
                // Filter markers based on localStorage filters
                setOriginalMarkers(data.result[0]);
                setOriginalLines(data.lines[0]);
                const filteredMarkers = filterMarkers(data.result[0]);
                const filteredLines = filterMarkers(data.lines[0]);
                setMarkers(filteredMarkers);
                setLines(filteredLines);
                setServiceData(data.result[0]);
                setIsLoading(false);
              }
            }
          }
        }
      } catch (err) {
        console.error("Error fetching hydrants:", err);
      }

      // Stop progress timer after data load
      clearInterval(progressInterval.current);

      // Retrieve highlighted markers from localStorage
      const stored = localStorage.getItem("highlightedMarkers");
      if (stored) {
        setHighlightedMarkers(stored.split(","));
      } else {
        setHighlightedMarkers([]);
      }
    })();

    // Cleanup on unmount
    return () => {
      mounted = false;
      clearInterval(progressInterval.current);
      clearInterval(refreshInterval.current);
    };
  }, [user]);

  // --------------------
  // Handlers / callbacks
  // --------------------
  const clearLines = (value) => {
    if (value) {
      setAddedLinesLatLng([]);
      setClearLinesTrigger(value);
    } else {
      setClearLinesTrigger(value);
    }
  };

  const removeLatLngLines = (index) => {
    setAddedLinesLatLng((prev) => prev.filter((_, i) => i !== index));
  };

  const showSearchForm = () => {
    closeForm();
    setShowSearchFormOnMap((prev) => !prev);
  };

  const closeSearchForm = () => {
    setShowSearchFormOnMap(false);
  };

  const shoeTheForm = () => {
    // Just open the form for adding/editing
    setMarkerData(markerData);
    setShowForm(true);
    setCancelMarker(true);
    setShowSearchFormOnMap(false);
  };

  const showTheTable = () => {
    setShowTable((prev) => !prev);
    // Possibly do your table resizing logic here
  };

  const onShowError = (error) => {
    setErrorText(error);
  };

  const onCancelError = () => {
    setErrorText(null);
  };

  const refreshData = useCallback(async () => {
    setRefreshing(true);
    clearLines(true);
    
    // Invalidate client-side cache before fetching fresh data
    invalidateAssetCache();
    
    try {
      const { data } = await axios.get("/api/hydrants");
      
      // Try to update sessionStorage cache with fresh data (may fail if too large)
      try {
        sessionStorage.setItem('assetData', JSON.stringify({
          result: data.result[0],
          lines: data.lines[0]
        }));
        sessionStorage.setItem('assetDataTimestamp', Date.now().toString());
      } catch (cacheErr) {
        console.warn('[Client Cache] Data too large for sessionStorage, skipping cache');
      }
      
      setOriginalLines(data.lines[0]);
      setOriginalMarkers(data.result[0]);
      const filteredMarkers = filterMarkers(data.result[0]);
      const filteredLines = filterMarkers(data.lines[0]);
      setMarkers(filteredMarkers);
      setLines(filteredLines);
      setShowForm(false);
      setMarkerData({});
      setCancelMarker(false);
      setServiceData(data.result[0]);
      setAddedLatLng([]);
      setAddedLinesLatLng([]);
      setShowFormAndAddMarker(false);
      setShowConfirmation(false);
      setRefreshing(false);
      setAddingPolyLine(false);
      addingPolyLineRef.current = false;
    } catch (err) {
      console.error("Error refreshing data:", err);
      setRefreshing(false);
    }

    // Restore map cursor
    const leafletGrab = document.getElementsByClassName("leaflet-grab")[0];
    if (leafletGrab) leafletGrab.style.cursor = "grab";

    const container = document.getElementsByClassName("leaflet-container")[0];
    if (container) container.style.height = "100%";
  }, []);

  const refreshDataWithOutClosing = async () => {
    setRefreshing(true);
    console.log("Updated setCancelMarker:", cancelMarker);
    setCancelMarker(true);
    setMovingMarker(false);
    clearLines(true);
    try {
      const { data } = await axios.get("/api/hydrants");
      setOriginalLines(data.lines[0]);
      setOriginalMarkers(data.result[0]);
      const filteredMarkers = filterMarkers(data.result[0]);
      const filteredLines = filterMarkers(data.lines[0]);
      setMarkers(filteredMarkers);
      setLines(filteredLines);

      // Keep the form open and update markerData if still relevant
      const updated = data.result[0].find(
        (x) => x[`${service}ID`] === markerData[`${service}ID`]
      );
      if (updated) {
        setMarkerData(updated);
      }
      setServiceData(data.result[0]);
      setShowConfirmation(false);
      setAddingPolyLine(false);
      addingPolyLineRef.current = false;
      setRefreshing(false);
    } catch (err) {
      console.error("Error refreshing data w/o closing:", err);
    }

    const leafletGrab = document.getElementsByClassName("leaflet-grab")[0];
    if (leafletGrab) leafletGrab.style.cursor = "grab";
  };

  const addMarkerToMap = (assetType, forceEnable = false) => {
    // Toggle form & add marker mode (or force enable if forceEnable is true)
    const leafletGrab = document.getElementsByClassName("leaflet-grab")[0];
    const container = document.getElementsByClassName("leaflet-container")[0];

    const willBeEnabled = forceEnable ? true : !showFormAndAddMarker;

    if (willBeEnabled) {
      if (leafletGrab) leafletGrab.style.cursor = "crosshair";
      if (container && window.innerWidth < 767) {
        container.style.height = "50%";
      }
    } else {
      if (leafletGrab) leafletGrab.style.cursor = "grab";
      if (container) container.style.height = "100%";
    }

    if (assetType.toLowerCase().includes("line")) {
      setAddedAssetType(assetType);
      setService(assetType);
      setAddingPolyLine(true);
      addingPolyLineRef.current = true; // Update ref immediately
      console.log("addMarkerToMap - Set addingPolyLine to true, ref updated to:", addingPolyLineRef.current);
    } else {
      if (forceEnable) {
        setShowFormAndAddMarker(true);
      } else {
        setShowFormAndAddMarker((prev) => !prev);
      }
      setAddedAssetType(assetType);
      setService(assetType);
      setAddingPolyLine(false);
      addingPolyLineRef.current = false; // Update ref immediately
      console.log("addMarkerToMap - Set addingPolyLine to false, ref updated to:", addingPolyLineRef.current);
    }
  };

  const EditMarker = (key, event) => {
    console.log("EditMarker - State value:", addingPolyLine);
    console.log("EditMarker - Ref value:", addingPolyLineRef.current);
    if (!addingPolyLineRef.current) {
      const assetType = key.markerData.assetType || "Hydrant";
      const matched = markers.find(
        (m) => m[`${assetType}ID`] === key.markerData[`${assetType}ID`]
      );
      const realService = assetType.match(/WaterMeter|TrafficSign|WaterLine|SewerLine/)
        ? assetType
        : assetType.charAt(0).toUpperCase() + assetType.slice(1).toLowerCase();

      setShowForm(true);
      setService(realService);
      setCancelMarker(false);
      setMarkerData(matched || key.markerData);
      setMarkerFormsProps({
        markerData: matched || key.markerData,
        deleteMarker,
        moveMarker,
        EditMarker,
      });
      setShowSearchFormOnMap(false);
    } else {
      // If currently adding a polyline, simulate a map click
      const mapEl = document.getElementById("mapCustom");
      if (mapEl) {
        const clickEvent = new MouseEvent("click", {
          view: window,
          bubbles: true,
          cancelable: true,
          clientX: event && event.originalEvent ? event.originalEvent.clientX : undefined,
          clientY: event && event.originalEvent ? event.originalEvent.clientY : undefined,
        });
        mapEl.dispatchEvent(clickEvent);
      }
    }
  };

  const SelectFromSearch = (asset) => {
    const assetType = asset.assetType || "Hydrant";
    const realService = assetType.match(/WaterMeter|TrafficSign|WaterLine|SewerLine/)
      ? assetType
      : assetType.charAt(0).toUpperCase() + assetType.slice(1).toLowerCase();

    const matched = markers.find((x) => x[`${assetType}ID`] === asset[`${assetType}ID`]);

    setShowForm(true);
    setService(realService);
    setCancelMarker(false);
    setMarkerData(matched || asset);

    setMarkerFormsProps({
      markerData: asset,
      deleteMarker,
      moveMarker,
      EditMarker,
    });
    setMapCenter([asset.Latitude, asset.Longitude]);
    setZoom(19);
    setIsLoadingLocation(true);

    // emulate small loading wait
    setTimeout(() => {
      setIsLoadingLocation(false);
    }, 500);
  };

  const moveMarker = (key, refresh) => {
    if (movingMarker && !refresh) {
      setResetMarker(true);
      setMarkerData(key);
      setMovingMarker((prev) => !prev);
      setCancelMarker(true);
    } else {
      setMarkerData(key);
      setMovingMarker((prev) => !prev);
      setResetMarker(false);
      setCancelMarker(false);
    }
  };

  const deleteMarker = (markerInfo) => {
    if (showConfirmation) {
      setShowConfirmation(false);
    } else {
      setShowConfirmation(true);
      setDeleteMarkerID(markerInfo);
    }
  };

  const confirm = async () => {
    try {
      await axios.post("/api/assestDEL", {
        asset: deleteMarkerID,
        assetType: service,
      });
      setCancelMarker(true);
      setMarkerData({});
      refreshData();
    } catch (err) {
      console.error("Error deleting marker:", err);
    }
  };

  const CallBackToParent = (latLng, newMarkers) => {
    setAddedLatLng(latLng);
    if (newMarkers) setMarkers(newMarkers);
  };

  // Callback from CustomMap when reverse geocoding completes after marker placement
  const onGeocodedAddress = (fields) => {
    setGeocodedFields(fields);
  };

  const CallBackToParentForLine = (latLngString, newLines) => {
    var latLngArray = []
    for (var i = 0; i < latLngString.length; i++) {
      latLngArray.push([latLngString[i].lat, latLngString[i].lng]);
    }
    setAddedLinesLatLng(latLngArray);
    if (newLines) setLines(newLines);
  };

  const CallFromAddToMapToCustomMap = (turnOffLines) => {
    if (turnOffLines) {
      setAddingPolyLine(false);
      addingPolyLineRef.current = false;
    }
  };

  // Callback for live preview of marker color changes in the edit form
  const onMarkerColorPreview = (globalId, color, assetType) => {
    setPreviewMarkerUpdate({ globalId, color, assetType, timestamp: Date.now() });
  };

  const ShowFullscreen = () => {
    setFullScreen((prev) => !prev);
  };

  const clearMarkerData = () => {
    // Clear markerData and set cancelMarker to match navbar "Add Asset" flow
    // This ensures CustomMap knows we're in "add new" mode, not edit mode
    setMarkerData({});
    setCancelMarker(true);
  };

  const closeForm = () => {
    // Note: addingPolyLine will be logged by useEffect when it actually changes
    const leafletGrab = document.getElementsByClassName("leaflet-grab")[0];
    if (leafletGrab) leafletGrab.style.cursor = "grab";

    const container = document.getElementsByClassName("leaflet-container")[0];
    if (container) container.style.height = "100%";

    setMarkerData({});
    setShowForm(false);
    setCancelMarker(true);
    setCloseAllPopups(false);
    setShowFormAndAddMarker(false);
    setShowSearchFormOnMap(false);
    setAddingPolyLine(false);
    addingPolyLineRef.current = false;
    setClearLinesTrigger(true);
    setAddedLinesLatLng([]);
    setGeocodedFields(null);
    // Note: addingPolyLine will be logged by useEffect when it actually changes
  };

  const centerMap = async () => {
    setIsLoadingLocation(true);
    const now = Date.now();
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const then = Date.now();
        console.log(
          "Time to get location:",
          then - now,
          pos.coords.latitude,
          pos.coords.longitude
        );
        setMapCenter([pos.coords.latitude, pos.coords.longitude]);
        setIsLoadingLocation(false);
        setZoom(19);
      },
      (err) => {
        console.error("Error getting geolocation:", err);
        setIsLoadingLocation(false);
      }
    );
  };

  const createReport = () => {
    setShowCreateReport(true);
  };

  const cancelReport = () => {
    setShowCreateReport(false);
  };

  const checkForHighlightedMarkers = useCallback((markersArray) => {
    setHighlightedMarkers((prev) => {
      const next = markersArray && markersArray.length ? markersArray : [];
      if (
        prev.length === next.length &&
        prev.every((id, i) => id === next[i])
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const removeHightLightForMarker = (id, editedData) => {
    setHighlightedMarkers((prev) => {
      const updated = prev.filter((x) => x !== id);
      localStorage.setItem("highlightedMarkers", updated.join(","));
      return updated;
    });
    setServiceData(editedData);

    const el = document.getElementById(id);
    if (el) {
      const parent = el.parentNode;
      parent.style.opacity = "";
      el.classList.remove("img-icon-selected");
    }
  };

  const removeHighlights = () => {
    highlightedMarkers.forEach((id) => {
      const child = document.getElementById(id);
      if (child) {
        const parent = child.parentNode;
        parent.style.opacity = "";
        child.classList.remove("img-icon-selected");
      }
    });
    setHighlightedMarkers([]);
    localStorage.removeItem("highlightedMarkers");
  };

  // Condition to wait for the user coords to be set
  const isUserCenterLoaded =
    geoCodedCenter[0] !== 36.144626 && geoCodedCenter[1] !== -97.291164;

  // ----------------------------
  // Render
  // ----------------------------
  return (
    <div style={{ overflow: "hidden", position: "fixed", width: "100%", height: "100%" }}>
      {/* Confirmation overlay */}
      {showConfirmation && (
        <div
          style={{
            position: "absolute",
            height: "100%",
            width: "100%",
            background: "#09090987",
            zIndex: 3002,
          }}
        >
          <Confirmation
            text="Are you sure you want to delete this?"
            confirm={confirm}
            deleteMarker={deleteMarker}
          />
        </div>
      )}

      {/* Create Report modal */}
      {showCreateReport && (
        <div
          style={{
            position: "absolute",
            height: "100%",
            width: "100%",
            background: "#09090987",
            zIndex: 3002,
          }}
        >
          <CreateReportModal
            text="Select a Report to Create?"
            ClientID={user.ClientID}
            allAssets={serviceData}
            cancelReport={cancelReport}
            isMobile={
              window.innerWidth < 767 ||
              document.documentElement.clientWidth < 767 ||
              document.body.clientWidth < 767
            }
          />
        </div>
      )}

      {/* Full-screen table */}
      {fullScreen && (
        <FullScreenTable
          dataTable={refreshDataWithOutClosing}
          ShowFullscreen={ShowFullscreen}
        />
      )}

      {/* Add to Map form */}
      {showForm && (
        <AddToMap
          data={markerData}
          serviceData={[...serviceData, ...lines]}
          markerFormsProps={markerFormsProps}
          assetType={service}
          user={user}
          addedLatLng={addedLatLng}
          addedLinesLatLng={addedLinesLatLng}
          addMarkerToMap={addMarkerToMap}
          cancel={closeForm}
          onShowError={onShowError}
          refreshData={refreshData}
          moveMarker={moveMarker}
          refreshDataWithOutClosing={refreshDataWithOutClosing}
          removeHightLightForMarker={removeHightLightForMarker}
          clearLinesFunc={clearLines}
          CallFromAddToMapToCustomMap={CallFromAddToMapToCustomMap}
          removeLatLngLines={removeLatLngLines}
          deleteMarker={deleteMarker}
          canWrite={canWrite}
          canDelete={canDelete}
          isReadOnly={isReadOnly}
          onMarkerColorPreview={onMarkerColorPreview}
          geocodedFields={geocodedFields}
          onClearMarkerData={clearMarkerData}
        />
      )}

      {/* Search form */}
      {showSearchFormOnMap && (
        <SearchModal
          data={markerData}
          serviceData={[...serviceData, ...lines]}
          assetType={service}
          user={user}
          addMarkerToMap={addMarkerToMap}
          SelectFromSearch={SelectFromSearch}
          cancel={closeSearchForm}
          checkForHighlightedMarkers={checkForHighlightedMarkers}
        />
      )}

      {/* Error modal */}
      {ErrorText && (
        <div
          style={{
            position: "absolute",
            height: "100%",
            width: "100%",
            background: "#09090987",
            zIndex: 3002,
          }}
        >
          <ErrorModal text={ErrorText} onCancelError={onCancelError} />
        </div>
      )}

      {/* Navigation bar */}
      <NavBar
        showForm={canWrite ? shoeTheForm : null}
        createReport={createReport}
        showSearchForm={showSearchForm}
        stillLoading={isLoading}
        user={user}
        setFilterMarkersFromNavbar={setFilterMarkersFromNavbar}
        canWrite={canWrite}
        isReadOnly={isReadOnly}
        roleName={roleName}
      />

      {/* Read-only mode banner */}
      {isReadOnly && (
        <div
          style={{
            position: "fixed",
            top: "74px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#ff9800",
            color: "#fff",
            padding: "8px 24px",
            borderRadius: "4px",
            zIndex: 2000,
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          🔒 Read-Only Mode - You can view assets but cannot make changes
        </div>
      )}

      {/* Location, refresh, camera, and remove-highlight are in CustomMap.jsx left sidebar */}

      {/* Main content: Map, optional table, etc. */}
      <div style={{ display: "flex", justifyContent: "center" }}>
        {isUserCenterLoaded ? (
          <div>
            <CustomMap
              zoom={zoom}
              markers={markers}
              lines={lines}
              addingMarker={showForm}
              mapCenter={
                mapCenter && mapCenter.length !== 0
                  ? mapCenter
                  : geoCodedCenter
              }
              showFormAndAddMarker={showFormAndAddMarker}
              addedAssetType={addedAssetType}
              movingMarker={movingMarker}
              editingData={markerData}
              CallBackToParent={CallBackToParent}
              cancelMarker={cancelMarker}
              EditMarker={EditMarker}
              moveMarker={moveMarker}
              resetMarker={resetMarker}
              markerLatLng={addedLatLng}
              addedLinesLatLng={addedLinesLatLng}
              CallBackToParentForLine={CallBackToParentForLine}
              deleteMarker={deleteMarker}
              closeAllPopups={closeAllPopups}
              isLoadingLocation={isLoadingLocation}
              addingPolyLine={addingPolyLine}
              clearLines={clearLinesTrigger}
              clearLinesFunc={clearLines}
              canWrite={canWrite}
              isReadOnly={isReadOnly}
              previewMarkerUpdate={previewMarkerUpdate}
              onGeocodedAddress={onGeocodedAddress}
              onQuickCaptureClick={() => {
                if (quickCaptureRef.current && quickCaptureRef.current.triggerCapture) {
                  quickCaptureRef.current.triggerCapture();
                }
              }}
              onCenterMap={centerMap}
              onRefreshData={refreshData}
              refreshing={refreshing}
              hasHighlightedMarkers={Boolean(highlightedMarkers && highlightedMarkers.length > 0)}
              onRemoveHighlights={removeHighlights}
            />
          </div>
        ) : (
          <div>
            <CircularProgress />
          </div>
        )}
      </div>

      {/* Quick Capture — camera button is now in CustomMap left sidebar */}
      {!showForm && !showSearchFormOnMap && canWrite && (
        <QuickCapture
          ref={quickCaptureRef}
          serviceData={[...serviceData, ...lines]}
          user={user}
          refreshData={refreshData}
          onShowError={onShowError}
          canWrite={canWrite}
        />
      )}
    </div>
  );
}

// Export with your caching HOC after defining App
const ClearCacheComponent = withClearCache(App);
export default ClearCacheComponent;
